<template>
  <div class="landing-page">
    <v-app-bar color="#0d1b2a" elevation="0" height="72" class="landing-app-bar">
      <img
        :src="rcsLogo"
        alt="RCS Consulting Solutions"
        class="landing-logo"
      />

      <v-spacer />

      <div class="d-flex align-center mr-3" style="gap: 4px">
        <v-menu location="bottom end">
          <template #activator="{ props }">
            <button type="button" class="user-chip" v-bind="props">
              <v-avatar size="28" color="white">
                <span class="text-caption font-weight-bold user-initial">
                  {{ userInitial }}
                </span>
              </v-avatar>
              <span class="user-email">{{ userEmail }}</span>
              <v-icon size="18" class="user-chevron">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list density="compact" min-width="220">
            <v-list-item :title="userEmail" subtitle="Signed in" />
            <v-divider />
            <v-list-item
              title="Logout"
              prepend-icon="mdi-logout"
              @click="logout"
            />
          </v-list>
        </v-menu>

      </div>
    </v-app-bar>

    <v-main>
      <TicketChatLogs
        v-if="selectedLogsTicket"
        :ticket="selectedLogsTicket"
        @back="selectedLogsTicket = null"
        @updated="handleLogsTicketUpdated"
      />

      <TicketReply
        v-else-if="selectedTicket"
        :ticket="selectedTicket"
        @back="selectedTicket = null"
        @updated="handleTicketUpdated"
        @deleted="handleTicketDeleted"
        @view-logs="openChatLogsFromTicket"
      />

      <div v-else class="table-container">
        <v-card flat class="pa-4 w-100 ticket-list-card">
          <div
            ref="tableToolbar"
            class="d-flex flex-wrap justify-space-between align-start mb-4"
            style="gap: 12px"
          >
            <div>
              <div class="cases-title">Cases</div>
              <div class="cases-subtitle">View and manage all support cases</div>
            </div>

            <div class="d-flex flex-wrap align-center toolbar-actions" style="gap: 8px">
              <v-text-field
                v-model="searchQuery"
                class="top-input"
                placeholder="Search cases..."
                density="compact"
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
              />

              <v-btn
                icon
                variant="outlined"
                class="filter-btn"
                aria-label="Filter cases"
              >
                <v-icon size="20">mdi-tune-variant</v-icon>
              </v-btn>

              <v-select
                v-model="selectedStatus"
                class="status-select"
                :items="statusFilterOptions"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
                variant="outlined"
                label="Status"
              />
            </div>
          </div>

          <div ref="tableWrap" class="mt-1">
            <v-data-table
              v-model:page="currentPage"
              v-model:items-per-page="itemsPerPage"
              :headers="headers"
              :items="tableLoading ? [] : filteredTickets"
              class="elevation-0 ticket-table"
              density="comfortable"
              :items-per-page-options="itemsPerPageOptions"
              :height="tableHeight"
              fixed-header
              :loading="tableLoading"
              :no-data-text="tableLoading ? '' : noDataText"
              :row-props="getRowProps"
              @click:row="handleRowClick"
            >
              <template #loading>
                <v-skeleton-loader class="mx-4 my-2" type="table-row@8" />
              </template>

              <template #[`item.caseId`]="{ item }">
                <span class="ticket-id">{{ item.caseId }}</span>
              </template>

              <template #[`item.subject`]="{ item }">
                <div class="subject-wrap">
                  <div class="subject-line">{{ item.subject }}</div>
                  <div v-if="item.meta" class="subject-meta">
                    <v-icon size="14">{{ metaIcon(item.meta) }}</v-icon>
                    <span>{{ item.meta }}</span>
                  </div>
                </div>
              </template>

              <template #[`item.status`]="{ item }">
                <span
                  class="status-chip"
                  :style="chipStyle(statusColorMap[item.status] || '#78909C')"
                >
                  <span
                    class="tone-dot"
                    :style="{ background: statusColorMap[item.status] || '#78909C' }"
                  />
                  {{ item.status }}
                </span>
              </template>

              <template #[`item.severity`]="{ item }">
                <span class="severity-cell">
                  <span
                    class="tone-dot"
                    :style="{ background: severityColorMap[item.severity] || '#78909C' }"
                  />
                  <span :style="{ color: severityColorMap[item.severity] || '#78909C' }">
                    {{ item.severity }}
                  </span>
                </span>
              </template>

              <template #[`item.created`]="{ item }">
                <div class="date-cell">
                  <v-icon size="16" color="grey">mdi-calendar-blank-outline</v-icon>
                  <span>{{ item.created }}</span>
                </div>
              </template>

              <template #[`item.updated`]="{ item }">
                <div class="date-cell">
                  <v-icon size="16" color="grey">mdi-clock-outline</v-icon>
                  <span>{{ item.updated }}</span>
                </div>
              </template>

              <template #[`item.actions`]="{ item }">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  class="text-none logs-btn"
                  prepend-icon="mdi-message-text-outline"
                  @click.stop="openChatLogs(item)"
                >
                  Logs
                </v-btn>
              </template>

              <template #bottom>
                <div class="table-bottom">
                  <span class="footer-range">{{ showingLabel }}</span>
                  <div class="d-flex align-center footer-controls" style="gap: 12px">
                    <span class="footer-label">Items per page:</span>
                    <v-select
                      v-model="itemsPerPage"
                      class="page-size-select"
                      :items="itemsPerPageOptions"
                      item-title="title"
                      item-value="value"
                      density="compact"
                      hide-details
                      variant="outlined"
                    /> <!--  -->
                    <v-pagination
                      v-model="currentPage"
                      :length="pageCount"
                      :total-visible="5"
                      density="compact"
                      active-color="primary"
                      rounded="0"
                    />
                  </div>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </div>
    </v-main>

    <DeleteTicket
      v-model="confirmDelete"
      :ticket="ticketToDelete"
      @deleted="handleTicketDeleted"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="top" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import TicketReply from "@/components/TicketManagement/TicketReply.vue";
import TicketChatLogs from "@/components/TicketManagement/TicketChatLogs.vue";
import DeleteTicket from "@/components/TicketManagement/DeleteTicket.vue";
import ListTickets from "@/mixins/ListTickets";
import GetTicket from "@/mixins/GetTicket";
import { mapChatbotLogs } from "@/mixins/ticketHelpers";
import rcsLogo from "@/assets/rcs_logo_v4 - Copy.png";

export default {
  name: "LandingPage",
  mixins: [ListTickets, GetTicket],
  components: {
    TicketReply,
    TicketChatLogs,
    DeleteTicket,
  },
  data() {
    return {
      rcsLogo,
      searchQuery: "",
      selectedStatus: "All",
      selectedTicket: null,
      selectedLogsTicket: null,
      tableLoading: true,
      detailLoading: false,
      windowHeight: window.innerHeight,
      tableTopOffset: 0,
      layoutObserver: null,
      itemsPerPage: 10,
      currentPage: 1,
      itemsPerPageOptions: [
        { value: 10, title: "10" },
        { value: 25, title: "25" },
        { value: 50, title: "50" },
        { value: 100, title: "100" },
        { value: -1, title: "All" },
      ],
      statusFilterOptions: [
        { title: "All", value: "All" },
        { title: "Awaiting RCS Support", value: "Awaiting RCS Support" },
        { title: "In progress", value: "In progress" },
        { title: "Resolved", value: "Resolved" },
        { title: "Auto-closed", value: "Auto-closed" },
      ],
      headers: [
        { title: "Case ID", key: "caseId", width: "180px", nowrap: true },
        { title: "Subject", key: "subject", minWidth: "280px" },
        { title: "Status", key: "status", width: "180px" },
        { title: "Severity", key: "severity", width: "120px" },
        { title: "Created", key: "created", width: "140px" },
        { title: "Last updated", key: "updated", width: "150px" },
        { title: "Action", key: "actions", sortable: false, width: "110px", align: "end" },
      ],
      tickets: [],
      confirmDelete: false,
      ticketToDelete: null,
      snackbar: {
        show: false,
        message: "",
        color: "error",
      },
    };
  },
  computed: {
    userEmail() {
      return this.$store.getters.userEmail;
    },
    userInitial() {
      return (this.userEmail || "?").charAt(0).toUpperCase();
    },
    noDataText() {
      return "No cases found.";
    },
    statusColorMap() {
      return {
        "Awaiting RCS Support": "#1E88E5",
        "In progress": "#FB8C00",
        "Awaiting your reply": "#FB8C00",
        Open: "#FB8C00",
        Resolved: "#43A047",
        "Auto-closed": "#78909C",
      };
    },
    severityColorMap() {
      return {
        Critical: "#E53935",
        High: "#FB8C00",
        Normal: "#43A047",
        Low: "#78909C",
      };
    },
    tableHeight() {
      const minHeight = 295;
      const viewportFillRatio = 0.91;
      const maxContentHeight = Math.floor(this.windowHeight * viewportFillRatio);
      const footerReserve = 72;

      if (!this.tableTopOffset) {
        return Math.max(minHeight, maxContentHeight - 100 - footerReserve);
      }

      return Math.max(minHeight, maxContentHeight - this.tableTopOffset - footerReserve);
    },
    filteredTickets() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.tickets.filter((ticket) => {
        const statusMatch =
          this.selectedStatus === "All" || ticket.status === this.selectedStatus;
        if (!statusMatch) return false;
        if (!q) return true;
        return [ticket.caseId, ticket.subject, ticket.meta, ticket.status, ticket.severity]
          .join(" ")
          .toLowerCase()
          .includes(q);
      });
    },
    pageCount() {
      const total = this.filteredTickets.length;
      if (!total) return 1;
      if (this.itemsPerPage === -1) return 1;
      return Math.max(1, Math.ceil(total / this.itemsPerPage));
    },
    rangeStart() {
      const total = this.filteredTickets.length;
      if (!total) return 0;
      if (this.itemsPerPage === -1) return 1;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    rangeEnd() {
      const total = this.filteredTickets.length;
      if (!total) return 0;
      if (this.itemsPerPage === -1) return total;
      return Math.min(this.currentPage * this.itemsPerPage, total);
    },
    showingLabel() {
      const total = this.filteredTickets.length;
      if (!total) return "Showing 0 to 0 of 0 cases";
      return `Showing ${this.rangeStart} to ${this.rangeEnd} of ${total} cases`;
    },
  },
  watch: {
    selectedStatus() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    pageCount(count) {
      if (this.currentPage > count) {
        this.currentPage = count;
      }
    },
  },
  async mounted() {
    this.measureTableLayout();
    window.addEventListener("resize", this.measureTableLayout);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.measureTableLayout);
    }

    this.layoutObserver = new ResizeObserver(() => {
      this.measureTableLayout();
    });

    this.$nextTick(() => {
      if (this.$refs.tableToolbar instanceof Element) {
        this.layoutObserver.observe(this.$refs.tableToolbar);
      }
      this.measureTableLayout();
    });

    await this.loadTickets();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.measureTableLayout);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.measureTableLayout);
    }
    if (this.layoutObserver) {
      this.layoutObserver.disconnect();
      this.layoutObserver = null;
    }
  },
  methods: {
    async logout() {
      await this.$store.dispatch("logout");
      this.$router.push({ name: "login" });
    },
    metaIcon(meta) {
      const map = {
        "Device integration": "mdi-monitor",
        Authentication: "mdi-lock-outline",
        Reporting: "mdi-file-chart-outline",
        Scheduling: "mdi-calendar-clock-outline",
        Media: "mdi-image-outline",
      };
      return map[meta] || "mdi-tag-outline";
    },
    showError(message) {
      this.snackbar = {
        show: true,
        message: message || "Something went wrong",
        color: "error",
      };
    },
    async loadTickets() {
      this.tableLoading = true;
      try {
        await this.fetchTickets();
        this.tickets = this.ticketsList;
      } catch (error) {
        console.error("Error loading tickets:", error);
        this.tickets = [];
        this.showError(error.message || "Failed to load cases");
      } finally {
        this.tableLoading = false;
        this.measureTableLayout();
      }
    },
    async handleRowClick(_event, row) {
      const item = this.unwrapTableItem(row?.item ?? row);
      if (!item) return;
      this.detailLoading = true;
      try {
        await this.fetchTicket(item.ticketId || item.ticket_id);
        const detail = this.ticketDetail || item;
        const listTicket = this.tickets.find((rowItem) => rowItem.ticketId === (item.ticketId || item.ticket_id)) || item;
        this.selectedTicket = {
          ...listTicket,
          ...detail,
          messages: detail.messages?.length ? detail.messages : listTicket.messages || [],
          chatbotLogs: this.resolveChatbotLogs(detail, listTicket, item),
        };
      } catch (error) {
        console.error("Error loading ticket detail:", error);
        this.selectedTicket = {
          ...item,
          chatbotLogs: this.resolveChatbotLogs(item),
        };
        this.showError(error.message || "Failed to load case details");
      } finally {
        this.detailLoading = false;
      }
    },
    handleTicketUpdated(updatedTicket) {
      const merged = {
        ...updatedTicket,
        chatbotLogs:
          updatedTicket.chatbotLogs?.length
            ? updatedTicket.chatbotLogs
            : this.selectedTicket?.chatbotLogs || [],
      };
      const index = this.tickets.findIndex(
        (t) => t.ticketId === merged.ticketId
      );
      if (index !== -1) {
        this.tickets.splice(index, 1, {
          ...this.tickets[index],
          ...merged,
          chatbotLogs: merged.chatbotLogs?.length
            ? merged.chatbotLogs
            : this.tickets[index].chatbotLogs || [],
        });
      }
      this.selectedTicket = merged;
    },
    handleLogsTicketUpdated(updatedTicket) {
      this.handleTicketUpdated(updatedTicket);
      this.selectedLogsTicket = this.withChatbotLogs({
        ...(this.selectedLogsTicket || {}),
        ...updatedTicket,
      });
    },
    handleTicketDeleted(ticket) {
      const deleted = ticket || this.ticketToDelete;
      this.tickets = this.tickets.filter((t) => t.ticketId !== deleted?.ticketId);
      this.selectedTicket = null;
      this.selectedLogsTicket = null;
      this.ticketToDelete = null;
      this.confirmDelete = false;
    },
    openChatLogs(ticket) {
      this.selectedTicket = null;
      this.selectedLogsTicket = this.withChatbotLogs(this.unwrapTableItem(ticket));
    },
    openChatLogsFromTicket(ticket) {
      this.selectedLogsTicket = this.withChatbotLogs(ticket || this.selectedTicket);
    },
    unwrapTableItem(item) {
      if (!item) return null;
      if (item.ticketId || item.caseId || item.chatbotLogs) return item;
      if (item.raw && (item.raw.ticketId || item.raw.caseId || item.raw.ticket_id || item.raw.chatbotLogs)) {
        return item.raw;
      }
      return item;
    },
    resolveChatbotLogs(...sources) {
      for (const src of sources) {
        if (!src) continue;
        if (Array.isArray(src.chatbotLogs) && src.chatbotLogs.length) {
          return src.chatbotLogs;
        }
        const mapped = mapChatbotLogs(
          src.chatbot_logs || src.raw?.chatbot_logs || src.raw?.chatbotLogs || []
        );
        if (mapped.length) return mapped;
      }
      const ticketId = sources.find((src) => src?.ticketId || src?.ticket_id);
      const fromList = this.tickets.find(
        (row) => row.ticketId === (ticketId?.ticketId || ticketId?.ticket_id)
      );
      return fromList?.chatbotLogs || [];
    },
    withChatbotLogs(ticket) {
      if (!ticket) return ticket;
      const chatbotLogs = this.resolveChatbotLogs(ticket, this.selectedTicket);
      return {
        ...ticket,
        chatbotLogs,
      };
    },
    openDeleteDialog(ticket) {
      this.ticketToDelete = ticket;
      this.confirmDelete = true;
    },
    measureTableLayout() {
      this.windowHeight = window.innerHeight;

      this.$nextTick(() => {
        const tableWrap = this.$refs.tableWrap;
        if (tableWrap instanceof Element) {
          this.tableTopOffset = tableWrap.getBoundingClientRect().top;
        }
      });
    },
    chipStyle(hex) {
      return {
        color: hex,
        background: `${hex}1A`,
      };
    },
    getRowProps({ item }) {
      const ticket = item?.status ? item : item?.raw ?? item;
      const status = ticket?.status;
      const accent = this.statusColorMap[status] || "#78909C";
      return {
        class: ["ticket-row", this.statusRowClass(status)],
        style: {
          "--row-accent": accent,
        },
      };
    },
    statusRowClass(status) {
      const map = {
        "In progress": "ticket-row--in-progress",
        "Awaiting your reply": "ticket-row--in-progress",
        Open: "ticket-row--in-progress",
        "Awaiting RCS Support": "ticket-row--awaiting",
        Resolved: "ticket-row--resolved",
        "Auto-closed": "ticket-row--closed",
      };
      return map[status] || "ticket-row--default";
    },
  },
};
</script>

<style scoped>
.landing-page {
  min-height: 100%;
  background: #f4f7f9;
}

.landing-page :deep(.v-main) {
  background: #f4f7f9;
}

.landing-app-bar {
  background: #0d1b2a !important;
  border-bottom: 0 !important;
}

.landing-logo {
  height: 76px;
  width: auto;
  max-width: 260px;
  margin: 0 12px 0 16px;
  object-fit: contain;
  display: block;
  flex: 0 0 auto;
  mix-blend-mode: screen;
}

.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  color: #fff;
}

.user-initial {
  color: #0d1b2a;
}

.user-email {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-chevron {
  color: rgba(255, 255, 255, 0.85);
}

.logout-btn {
  color: #fff !important;
}

.ticket-list-card {
  background: transparent;
}

.cases-title {
  font-size: 1.65rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.cases-subtitle {
  margin-top: 4px;
  font-size: 0.92rem;
  color: #8b95a5;
}

.toolbar-actions {
  align-items: center;
}

.top-input {
  width: 240px;
}

.filter-btn {
  height: 40px !important;
  width: 40px !important;
  border-color: #d5dbe3 !important;
  color: #5f6b7a !important;
}

.status-select {
  width: 168px;
}

.ticket-table {
  background: transparent !important;
}

.table-bottom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 4px 4px;
}

.footer-range {
  font-size: 13px;
  color: #6b7280;
}

.footer-label {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

.page-size-select {
  width: 88px;
}

.ticket-table :deep(.v-pagination__item--is-active .v-btn) {
  border: 1px solid #1976d2;
  background: transparent !important;
  color: #1976d2 !important;
}

.ticket-table :deep(.v-table__wrapper) {
  overflow: auto;
}

.ticket-table :deep(table) {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

.ticket-table :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 5;
}

.ticket-table :deep(thead th) {
  background: #f4f7f9 !important;
  color: #9aa3b2 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-bottom: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 12px !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 5 !important;
  box-shadow: none;
}

.ticket-table :deep(tbody tr) {
  cursor: pointer;
  background: #fff !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.ticket-table :deep(tbody tr:hover) {
  background: #fafbfc !important;
}

.ticket-table :deep(tbody td) {
  border-top: 1px solid #eef1f5 !important;
  border-bottom: 1px solid #eef1f5 !important;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  background: inherit;
}

/* Row gap without breaking sticky header */
.ticket-table :deep(tbody tr + tr td) {
  border-top-width: 10px !important;
  border-top-color: #f4f7f9 !important;
}

.ticket-table :deep(tbody td:first-child) {
  position: relative;
  overflow: hidden;
  border-left: 0 !important;
  border-radius: 12px 0 0 12px;
  padding-left: 18px !important;
}

.ticket-table :deep(tbody td:first-child)::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: var(--row-accent, #78909c);
  border-radius: 12px 0 0 12px;
}

.ticket-table :deep(tbody tr.ticket-row--in-progress td:first-child)::before {
  background: #fb8c00;
}

.ticket-table :deep(tbody tr.ticket-row--awaiting td:first-child)::before {
  background: #1e88e5;
}

.ticket-table :deep(tbody tr.ticket-row--resolved td:first-child)::before {
  background: #43a047;
}

.ticket-table :deep(tbody tr.ticket-row--closed td:first-child)::before,
.ticket-table :deep(tbody tr.ticket-row--default td:first-child)::before {
  background: #78909c;
}

.ticket-table :deep(tbody td:last-child) {
  border-right: 1px solid #eef1f5 !important;
  border-radius: 0 12px 12px 0;
}

.ticket-id {
  font-weight: 700;
  color: #212121;
  white-space: nowrap;
  display: inline-block;
}

.subject-wrap {
  min-width: 0;
  max-width: 100%;
}

.subject-line {
  font-weight: 600;
  color: #212121;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subject-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  color: #8b95a5;
  font-size: 12px;
}

.logs-btn {
  font-weight: 600 !important;
  letter-spacing: 0;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.tone-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.severity-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.date-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .top-input,
  .status-select {
    width: 100%;
  }

  .user-email,
  .user-chevron {
    display: none;
  }

  .table-bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
